import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useFilterStore} from './filter-store'
import { searchEndpoint, productEndpoint, articleEndpoint } from '../services/nopAPI';

export const useSearchStore = defineStore('search', () => {
  const searchedString = ref('');
  const isDropDownSearch = ref(false);

  const productResults = ref([]);
  const L2productResults = ref([]);
  const pricingResults = ref([]);

  const articleResults = ref([]);
  const searchLoading = ref(false);
  const articleImgResults = ref([]);

  const dropdownArticleResults = ref([]);
  const dropdownArticleImgResults = ref([]);
  
  const productL1FeedBack = ref([]);
  const productL2FeedBack = ref([]);
  const productDocumentTypeFeedBack = ref([]);
  
  const productsPerPage = ref(10);

  const articlesPerPage = ref(3);
  const dropdownArticlesPerPage = ref(10);

  const startPage = ref(1);

  const articlesStartPage = ref(1);
  const dropdownArticlesStartPage = ref(1);

  const productsSortBy = ref('_score+desc');
  const parserOptions = ref('&q.parser=structured&q.options=%7B%22defaultOperator%22%3A%22and%22%2C%22fields%22%3A%5B%22sku%5E100%22%2C%20%22name%5E50%22%2C%20%22shortdescription%5E10%22%2C%20%22fulldescription%5E4%22%5D%7D');
  const feedbackParserOptions = ref('&q.parser=structured&facet.l1_=%7B%7D&facet.l2_=%7B%7D&facet.documenttype=%7B%7D&q.options=%7B%22fields%22%3A%5B%22fulldescription%22%2C%22shortdescription%22%2C%22name%22%2C%22sku%22%5D%7D');
  const searchQuery = ref('');

  const { formattedFilters, isProductSearch, documentTypeFilters, isArticleSearch, appliedProductFilters, appliedArticleFilters, isCategorySearch } = storeToRefs(useFilterStore());
  const { parseOutQField, buildQField } = useFilterStore()

  const productMatchCount = computed(() => {
    if (productResults.value.hits && productResults.value.hits.found) {
      return productResults.value.hits.found;
    }
    return 0;
  });

  const articleMatchCount = computed(() => {
    if (articleResults.value.hits && articleResults.value.hits.found) {
      return articleResults.value.hits.found;
    }
    return 0;
  });

  const dropdownArticleMatchCount = computed(() => {
    if (dropdownArticleResults.value.hits && dropdownArticleResults.value.hits.found) {
      return dropdownArticleResults.value.hits.found;
    }
    return 0;
  });

  const productIdList = computed(() => {
    if (productMatchCount.value) {
      return productResults.value.hits.hit.map((hit) => hit.fields.documenttype_id);
    }
    return [];
  });

  const articleIdList = computed(() => {
    if (articleMatchCount.value) {
      return articleResults.value.hits.hit.map((hit) => hit.fields.documenttype_id);
    }
    return [];
  });
  const dropdownArticleIdList = computed(() => {
    if (dropdownArticleMatchCount.value) {
      return dropdownArticleResults.value.hits.hit.map((hit) => hit.fields.documenttype_id);
    }
    return [];
  });

  const productSearchResults = computed(() => {
    if (productResults.value.hits && productResults.value.hits.found) {
      return productResults.value.hits.hit.map((item) => {
        const id = item.fields.documenttype_id;
        const defaultPricing = {
          pricing: {
            StockQuantity: null,
            ProductsPrice: null,
          },
        };
        const pricing = { pricing: pricingResults.value[id] } || defaultPricing;
        const match = { id, ...item.fields, ...pricing };
        return match;
      });
    }
    return [];
  });

  const articleSearchResults = computed(() => {
    if (articleResults.value.hits && articleResults.value.hits.found) {
      /* eslint-disable */
      return articleResults.value.hits.hit.map((item) => {
        const id = item.fields.documenttype_id;
        const defaultDetails = {
          details: {
            img: null,
            posted: 'Unknown',
          },
        };
        const details = articleImgResults.value[id] ? { details: articleImgResults.value[id] } : defaultDetails;
        const match = { id, ...item.fields, ...details };
        return match;
      });
    }
    return [];
  });

  const dropdownArticleSearchResults = computed(() => {
    if (dropdownArticleResults.value.hits && dropdownArticleResults.value.hits.found) {
      /* eslint-disable */
      return dropdownArticleResults.value.hits.hit.map((item) => {
        const id = item.fields.documenttype_id;
        const defaultDetails = {
          details: {
            img: null,
            posted: 'Unknown',
          },
        };
        const details = dropdownArticleImgResults.value[id] ? { details: dropdownArticleImgResults.value[id] } : defaultDetails;
        const match = { id, ...item.fields, ...details };
        return match;
      });
    }
    return [];
  });

  const L2productSearchResults = computed(() => {
    const imgURL="/images/thumbs/0017241_nitrile-70-durometer-md-o-ring-270.jpeg";
    const search_String=searchedString.value;

    let searchResult=L2productResults.value.map(item => {
      let searchLink=search_String ? `/search?q=${search_String}&l2=${item.value}` : '#';
      return {
        ...item,
        image:imgURL,
        link: searchLink,
      }
    })
    return searchResult;
  });

  async function initializeSearchPage() {
    let query = window.location.search;
    if (query) {
      const params = new URLSearchParams(query);
      searchedString.value = parseOutQField()

      if (params.get('start')) {
        startPage.value = parseInt(params.get('start'))
      }
      if (params.get('size')) {
        productsPerPage.value = params.get('size')
      }
      if (params.get('sort')) {
        productsSortBy.value = params.get('sort')
      }

      if (query.charAt(0) === '?') {
        query = query.slice(1);
      }
    }
  }

  async function getFacetOptionList(facetList = [], l2Name = '') {
    if (!facetList.length) return [];

    const facetOptions = facetList.reduce((acc, facet) => {
      acc += `&facet.${facet}={size:999}`;
      return acc;
    }, '');

    var l2Filter = l2Name ? `&fq=(term field%3Dl2_ '${l2Name}')` : '';
    
    const { facets } = await searchEndpoint.search(`q=(matchall)&q.parser=structured${facetOptions}${l2Filter}`);
    return facets;
  }

  async function searchProducts() {
    if (isProductSearch.value) {
      // let allAppliedFilters = '';
      // if (formattedFilters.value.length) {
      //   allAppliedFilters = `(and${documentTypeFilters.value['product']}${formattedFilters.value}})`;
      // } else {
      //   allAppliedFilters = documentTypeFilters.value['product'] ? `${documentTypeFilters.value['product']}` : '';
      // }

      // allAppliedFilters = allAppliedFilters ? `&fq=${allAppliedFilters}` : '';
      console.log('product searching..');

      if (isCategorySearch.value) {
        const size = productsPerPage.value === 10 ? '' : `&size=${productsPerPage.value}`;
        const start = startPage.value === 1 ? '' : `&start=${startPage.value}`;
        let query = `q=(matchall)${parserOptions.value}${size}${start}&fq=${appliedProductFilters.value}`;

        await getProductsByL2Category();
        const newUrl = `${window.location.origin}${window.location.pathname}?${query}`;
        window.history.replaceState({}, '', newUrl);
      } else if (searchedString.value) {
        const size = productsPerPage.value === 10 ? '' : `&size=${productsPerPage.value}`;
        const start = startPage.value === 1 ? '' : `&start=${startPage.value}`;
      
        let query = `q=(or${buildQField(searchedString.value)})${parserOptions.value}${size}${start}&fq=${appliedProductFilters.value}`;
        await getProductsBySearchString(searchedString.value);
        const newUrl = `${window.location.origin}${window.location.pathname}?${query}`;
        window.history.replaceState({}, '', newUrl);
      }
      //productResults.value = await searchEndpoint.search(query)
      //getProductPriceAndStock();
    }
  }

  async function searchFeedBack() {
    let query = `q=(or${buildQField(searchedString.value)})${feedbackParserOptions.value}&size=0`;
    const { facets } = await searchEndpoint.search(query);
    if (facets !== undefined) {
      productL1FeedBack.value = facets["l1_"]['buckets'];
      productL2FeedBack.value = facets["l2_"]['buckets'];
      productDocumentTypeFeedBack.value = facets["documenttype"]['buckets'];
    }
    // const result = await searchEndpoint.search(query);
  }

  async function getProductsBySearchString(searchString){
    let allAppliedFilters = '';
      if (formattedFilters.value.length) {
        allAppliedFilters = `(and${documentTypeFilters.value['product']}${formattedFilters.value}})`;
      } else {
        allAppliedFilters = documentTypeFilters.value['product'] ? `${documentTypeFilters.value['product']}` : '';
      }

      if (!searchString) return
      let qField = `(or${buildQField(searchString)})`
      allAppliedFilters = allAppliedFilters ? `&fq=${allAppliedFilters}` : '';

      const size = productsPerPage.value === 10 ? '' : `&size=${productsPerPage.value}`;
      const start = startPage.value === 1 ? '' : `&start=${startPage.value}`;
      let query = `q=${qField}&q.parser=structured${size}${start}&fq=${appliedProductFilters.value}`;
      productResults.value = await searchEndpoint.search(query)
      getProductPriceAndStock()
  }

  async function getProductsByL2Category() {
    const size = productsPerPage.value === 10 ? '' : `&size=${productsPerPage.value}`;
    const start = startPage.value === 1 ? '' : `&start=${startPage.value}`;
    let query = `q=(matchall)&q.parser=structured${size}${start}&fq=${appliedProductFilters.value}`;
    productResults.value = await searchEndpoint.search(query)
    getProductPriceAndStock();
  }

  async function searchArticles() {
    if (!isDropDownSearch.value && searchedString.value && isArticleSearch.value) {
      const size = articlesPerPage.value === 10 ? '' : `&size=${articlesPerPage.value}`;
      const start = articlesStartPage.value === 1 ? '' : `&start=${articlesStartPage.value}`;
      const returnFields = `&return=tags,documenttype,name,shortdescription,documenttype_id,slug`;
      const query = `q=(or${buildQField(searchedString.value)})&q.parser=structured&fq=${appliedArticleFilters.value}${returnFields}${size}${start}`;

      articleResults.value = await searchEndpoint.search(query)
      console.log("search articles call multiple times.");
      getArticleDetail()
    }
  }

  async function searchDropdownArticles(KBaseType='blog') {
    // console.log('drop down search',isDropDownSearch.value);
    if (isDropDownSearch.value && searchedString.value) {
      const returnFields = `&return=tags,documenttype,name,shortdescription,documenttype_id,slug`;
      const query = `q=(or${buildQField(searchedString.value)})&q.parser=structured&fq=(and(term field%3Ddocumenttype '${KBaseType}'))${returnFields}`

      const articleRes = await searchEndpoint.search(query)
      dropdownArticleResults.value = articleRes
      getDropdownArticleDetail()
    }
  }


  async function getProductPriceAndStock() {
    if (productMatchCount.value) {
      // productIdList.value.map(async (id) => {
      //   if (pricingResults.value[id] && pricingResults.value[id].loaded) return
      //   await productEndpoint.getProductPriceAndStock([id])
      //     .then((priceAndStock) => {
      //       const stuff = {
      //         loaded: true,
      //         id: priceAndStock.ProductsPricing[0].Id,
      //         ProductsPrice: priceAndStock.ProductsPricing[0].ProductsPrice,
      //         StockQuantity: priceAndStock.ProductsPricing[0].StockQuantity,
      //         ShowStock: priceAndStock.ProductsPricing[0].ShowStock,
      //         StockStatus: priceAndStock.ProductsPricing[0].StockStatus
      //       };
      //       pricingResults.value[id] = stuff
      //     })
      //     .catch(() => {
      //       const stuff = {
      //         loaded: false,
      //         id,
      //         ProductsPrice: 'Call for price',
      //         StockQuantity: 'Call for stock',
      //       };
      //       pricingResults.value[id] = stuff;
      //     })
      // })
      // productIdList.value.map(async (id) => {
      //   if (pricingResults.value[id] && pricingResults.value[id].loaded) return
      // })
      // console.log(pricingResults.value.length);
      // if(pricingResults.value.length > 0) return;

      await productEndpoint.getProductPriceAndStock([productIdList.value])
        .then((priceAndStock) => {
          productIdList.value.map(async (id,index) => {
            const stuff = {
              loaded: true,
              id: priceAndStock.ProductsPricing[index].Id,
              ProductsPrice: priceAndStock.ProductsPricing[index].ProductsPrice,
              StockQuantity: priceAndStock.ProductsPricing[index].StockQuantity,
              ShowStock: priceAndStock.ProductsPricing[index].ShowStock,
              StockStatus: priceAndStock.ProductsPricing[index].StockStatus
            };
            pricingResults.value[id] = stuff
          })
        })
        .catch(() => {
          productIdList.value.map(async (id,index) => {
            const stuff = {
              loaded: false,
              id,
              ProductsPrice: 'Call for price',
              StockQuantity: 'Call for stock',
            };
            pricingResults.value[id] = stuff;
          })
        })
    }
  }
  
  async function getArticleDetail() {
    if (articleMatchCount.value) {
      const defaultDetail = {
        loaded: false,
        id: 0,
        img: null,
        posted: 'Unknown',
      };
      await articleEndpoint.getArticleDetail([articleIdList.value])
      .then((detailData) => {
        articleIdList.value.map(async (id, index) => {
          const detail = {
            loaded: true,
            id: detailData.Articles[index].id,
            img: detailData.Articles[index].img,
            posted: detailData.Articles[index].posted,
          };
          articleImgResults.value[id] = detail;
        })
      })
      .catch(() => {
        articleImgResults.value[id] = defaultDetail;
      });
      // articleIdList.value.map(async (id) => {
      //   if (articleImgResults.value[id] && articleImgResults.value[id].loaded) return;
      //   const defaultDetail = {
      //     loaded: false,
      //     id,
      //     img: null,
      //     posted: 'Unknown',
      //   };
      //   await articleEndpoint
      //     .getArticleDetail([id])
      //     .then((detailData) => {
      //       const detail = {
      //         loaded: true,
      //         id: detailData.Article.id,
      //         img: detailData.Article.img,
      //         posted: detailData.Article.posted,
      //       };
      //       articleImgResults.value[id] = detail;
      //     })
      //     .catch(() => {
      //       articleImgResults.value[id] = defaultDetail;
      //     });
      // });
    }
  }
  async function getDropdownArticleDetail() {
    if (dropdownArticleMatchCount.value) {
      if (dropdownArticleImgResults.value.length > 0) return;
      const defaultDetail = {
        loaded: false,
        id: 0,
        img: null,
        posted: 'Unknown',
      };
      await articleEndpoint.getArticleDetail([dropdownArticleIdList.value])
      .then((detailData) => {
        dropdownArticleIdList.value.map(async (id, index) => {
          const detail = {
            loaded: true,
            id: detailData.Articles[index].id,
            img: detailData.Articles[index].img,
            posted: detailData.Articles[index].posted,
          };
          dropdownArticleImgResults.value[id] = detail;
        })
      })
      .catch(() => {
        dropdownArticleImgResults.value[id] = defaultDetail;
      });
      // dropdownArticleIdList.value.map(async (id) => {
      //   if (dropdownArticleImgResults.value[id] && dropdownArticleImgResults.value[id].loaded) return;
      //   const defaultDetail = {
      //     loaded: false,
      //     id,
      //     img: null,
      //     posted: 'Unknown',
      //   };
      //   await articleEndpoint
      //     .getArticleDetail([id])
      //     .then((detailData) => {
      //       const detail = {
      //         loaded: true,
      //         id: detailData.Article.id,
      //         img: detailData.Article.img,
      //         posted: detailData.Article.posted,
      //       };
      //       dropdownArticleImgResults.value[id] = detail;
      //     })
      //     .catch(() => {
      //       dropdownArticleImgResults.value[id] = defaultDetail;
      //     });
      // });
    }
  }
  const executeSearch = async () => {
    console.log('SEARCH EXECUTED');
  }

  async function getL2ProductResults(l1ProductName){
    if (searchedString.value) {
      l1ProductName = l1ProductName.replace(/&/g, '%26');
      let query = `q=(or${buildQField(searchedString.value)})&q.parser=structured&fq=l1_:'${l1ProductName}'&q.options={"fields":["fulldescription","shortdescription","name","sku"]}&facet.l2_={}`;
      const l2productsRes = await searchEndpoint.search(query);
      const L2products = l2productsRes?.facets?.l2_?.buckets;
      if (Array.isArray(L2products)) {
        L2productResults.value = L2products;
      }
    }
  }


  function debounce(fn, delay) {
    let timeoutId = null;
    return async function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        timeoutId = null;
        await fn(...args);
      }, delay);
    };
  }

  function setLoading() {
    searchLoading.value = true
  }

  function unSetLoading() {
    searchLoading.value = false
  }

  return {
    initializeSearchPage,
    productSearchResults,
    articleSearchResults,
    searchedString,
    isDropDownSearch,
    getProductPriceAndStock,
    searchProducts,
    searchLoading,
    productL1FeedBack,
    productL2FeedBack,
    productDocumentTypeFeedBack,
    productIdList,
    productMatchCount,
    articleMatchCount,
    productsPerPage,
    startPage,
    searchArticles,
    getL2ProductResults,
    L2productSearchResults,
    articlesPerPage,
    articlesStartPage,
    articleImgResults,
    articleIdList,
    searchQuery,
    getFacetOptionList,

    dropdownArticlesPerPage,
    dropdownArticlesStartPage,
    dropdownArticleMatchCount,
    dropdownArticleIdList,
    dropdownArticleSearchResults,
    searchDropdownArticles,
    getDropdownArticleDetail,

    debounce,
    getProductsBySearchString,
    searchFeedBack,
    setLoading,
    unSetLoading,
  };
});